<template>
  <transition name="slide">
    <div v-if="uiStore.mobileMenu" class="sidebar">
      <div class="sidebar-backdrop" @click="closeSidebarPanel"></div>
      <div v-if="uiStore.mobileMenu" class="sidebar-panel">
        <slot>
          <div v-if="isLoggedIn" id="mobile-account-data">
            <div class="user-login">
              <i class="fa fa-user" style="color: white;"></i>
            </div>
            <div id="mobile-account-credentials">
              <div id="mobile-account-name">{{ userName }}</div>
            </div>
          </div>
          <nav>
            <NuxtLink
              v-for="item in mainMenuItems"
              @click="closeSidebarPanel"
              :key="item.id"
              :to="item.attributes.link"
              :title="item.attributes.label"
              active-class="nav-active" 
            >
              <span v-html="item.attributes.label"></span>
            </NuxtLink>
            <NuxtLink active-class="nav-active" to="/cart" @click="closeSidebarPanel" class="cart-logo">
              <span v-if="cartProductsLength" class='cart-badge cart-badge-warning' id='cart-count' v-html="cartProductsLength"></span>
              <i class="fa fa-shopping-cart" style="padding-right: 0.5rem"></i>Cart 
            </NuxtLink>
            <NuxtLink NuxtLink active-class="nav-active" to="/account" @click="checkLogin" class="user-login" style="border-bottom: 1px solid #444;">
              <i class="fa fa-user" style="padding-right: 0.5rem"></i>Account
            </NuxtLink>
          </nav>
          <!-- Keeping the logout as an anchor tag since it's an action, not navigation -->
          <a v-if="isLoggedIn" @click="logOut" style="margin-top: 2rem;" href="#" class="mobile-signout fa-btn-secondary"> <!-- Using isLoggedIn -->
            <span style="margin-left: 1rem;">Sign out</span>
          </a>
        </slot>
      </div>
    </div>
  </transition>
</template>

<script setup lang="ts">
import { ref, onMounted, computed } from 'vue';  // <-- Import the `computed` function
import { useShopStore } from '~/store/shopStore';
import { useUserStore } from '~/store/userStore';
import { useUIStore } from '~/store/uiStore';
import { ModalTypes } from '@/types/enums';
import type { MenuItem } from '~/types/index';

const shopStore = useShopStore();
const userStore = useUserStore();
const uiStore = useUIStore();

// Existing user data ref
const userData = ref(userStore.userData);
const cartProductsLength = ref(shopStore.cart?.products?.length || 0);

// Computed properties for user's logged-in state and email
const isLoggedIn =  computed(() => userStore.userData?.is_logged_in || false);
const userName = computed(() => userStore.userData?.name || '');
const mainMenuItems = computed(() => uiStore.mainMenuItems || '');

const closeSidebarPanel = async () => {
  uiStore.setMobileMenu(false);
};

const checkLogin = () => {
  if (!isLoggedIn.value) {
    uiStore.setModalType(ModalTypes.LOGIN);
  }
  closeSidebarPanel();
};

import { useLogoutAndClearCart } from '~/composables/useLogout';

const { logoutAndClearCart } = useLogoutAndClearCart();

const logOut = async () => {
  logoutAndClearCart();
};

</script>

<style scoped lang="scss">
@import '~/assets/constants.scss';
.nav-active
{
  display: none;
}

#mobile-account-name
{
  color: $light-white;
}

.slide-enter-active, .slide-leave-active {
  transition: transform 0.2s ease;
}

.slide-enter, .slide-leave-to {
  transform: translateX(-100%);
  transition: all 150ms ease-in 0s;
}

.sidebar-backdrop {
  background-color: rgba(0, 0, 0, 0.5);
  width: 100vw;
  height: 100vh;
  position: fixed;
  top: 0;
  left: 0;
  cursor: pointer;
  z-index: 99996;
}

.sidebar-panel {
  overflow-y: auto;
  background: linear-gradient(#0a0a0a, #232323);
  box-shadow: 0 5px 7px rgba(0, 0, 0, 0.9);
  position: fixed;
  left: 0;
  top: 0;
  height: 100vh;
  z-index: 99997;
  padding: 3rem 20px 2rem;
  width: 300px;
  min-width: 300px;

  a {
    color: $light-white;
    font-weight: 400;
    width: 100%;
    display: inline-block;
    text-align: left;
    border-top: 1px solid #444;
    padding-top: 0.75rem;
    padding-bottom: 0.75rem;
  }
}

#mobile-account-data {
  display: grid;
  grid-template-columns: 1fr 3fr;

  .user-login::before {
    font-size: 3.5rem;
  }

  #mobile-account-name {
    color: $light-white;
    font-weight: bold;
  }

  #mobile-account-mail {
    font-style: italic;
    font-size: 1.25rem;
  }
}

.mobile-signout.fa-btn-secondary {
  border-radius: $fa-border-radius;
}

</style>
